import React, { useState, useEffect, useCallback } from 'react';
import { 
  Button, Modal, Form, Table, Row, Col, Card, Alert, Spin, 
  Input, Pagination, Switch, Checkbox, Typography, Space,
  Tag, Tooltip, message
} from 'antd';
import { 
  PlusOutlined, DeleteOutlined, KeyOutlined, CloudServerOutlined, 
  UserAddOutlined, UserDeleteOutlined, SettingOutlined, 
  LoginOutlined, LogoutOutlined, EyeOutlined, LockOutlined, 
  BuildOutlined, QuestionCircleOutlined, ArrowLeftOutlined, 
  ExclamationCircleOutlined, SafetyOutlined, BookOutlined,
  CrownOutlined, GlobalOutlined, InfoCircleOutlined, BulbOutlined,
  ClockCircleOutlined, CopyOutlined, ArrowRightOutlined,
  CheckCircleOutlined, PlusCircleOutlined, MinusCircleOutlined, CloseOutlined
} from '@ant-design/icons';
import API from '../services/api';
import usePermissionCheck from '../hooks/usePermissionCheck';
import PermissionDenied from '../components/PermissionDenied';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;

const RAMAccountManagement = () => {
  // 这里我们需要检查用户是否为主账号，而不是特定的权限
  // 因为RAM账号管理是主账号的专属功能
  const { userAccountType, isLoading: permissionLoading } = usePermissionCheck();
  
  const [loading, setLoading] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState(null); // 新增：当前用户角色
  
  // 新增：RAM账号列表分页和搜索状态
  const [ramListData, setRamListData] = useState({
    ramUsers: [],
    totalRAMUsers: 0,
    currentPage: 1,
    totalPages: 1
  });
  const [ramSearchTerm, setRamSearchTerm] = useState('');
  const [ramLoading, setRamLoading] = useState(false);
  const ramLimit = 10; // 每页显示的RAM账号数量

  // 创建RAM账号相关状态
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    password: '',
    qq: '', // 新增QQ号字段
    allowMultipleLogin: false, // 新增多点登录字段
    permissions: {
      canViewIPList: true,
      canViewBigWallTraffic: true,
      canViewIPConnections: true,
      canViewSmallWallLogs: true,
      canViewAttackRecords: true,
      canManageIPs: false,
      canExportReports: false,
      canModifySettings: false
    }
  });

  // 修改权限相关状态
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedRAMUser, setSelectedRAMUser] = useState(null);
  const [permissionsForm, setPermissionsForm] = useState({});

  // IP分配相关状态
  const [showIPModal, setShowIPModal] = useState(false);
  const [selectedIPs, setSelectedIPs] = useState([]);
  const [ipAction, setIpAction] = useState('assign'); // 'assign' 或 'revoke'
  const [ipSearchTerm, setIpSearchTerm] = useState('');
  const [ipListData, setIpListData] = useState({
    ips: [],
    totalIps: 0,
    currentPage: 1,
    totalPages: 1
  });
  const [ipLoading, setIpLoading] = useState(false);
  const ipLimit = 10; // 每页显示的IP数量

  // 删除确认相关状态
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // 权限详情查看相关状态
  const [showPermissionDetailModal, setShowPermissionDetailModal] = useState(false);
  const [selectedRAMUserForDetail, setSelectedRAMUserForDetail] = useState(null);

  // 页面显示控制状态
  const [currentView, setCurrentView] = useState('main'); // 'main' 或 'tips'

  // 企业域管理相关状态
  const [showCompanyDomainModal, setShowCompanyDomainModal] = useState(false);
  const [companyDomainForm, setCompanyDomainForm] = useState('');
  const [currentCompanyDomain, setCurrentCompanyDomain] = useState('');
  const [companyDomainLoading, setCompanyDomainLoading] = useState(false);

  // 企业域验证相关状态
  const [domainVerificationStep, setDomainVerificationStep] = useState('input'); // 'input', 'verification', 'verify'
  const [verificationRecord, setVerificationRecord] = useState('');
  const [verificationExpiration, setVerificationExpiration] = useState('');
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [isDomainVerified, setIsDomainVerified] = useState(false);

  // 密码重置相关状态
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedRAMUserForPassword, setSelectedRAMUserForPassword] = useState(null);
  const [newPasswordForm, setNewPasswordForm] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // 多点登录确认相关状态
  const [showMultiLoginModal, setShowMultiLoginModal] = useState(false);
  const [multiLoginAction, setMultiLoginAction] = useState(null);

  // 时间格式化函数 - 避免时区转换问题
  const formatTime = (timeString) => {
    if (!timeString) return '从未登录';
    
    // 如果时间字符串已经是友好格式，直接返回
    if (timeString.includes('年') || timeString.includes('/')) {
      return timeString;
    }
    
    // 处理ISO格式的时间字符串，但避免时区转换
    try {
      // 移除时区信息，直接按照字面值解析
      const cleanTime = timeString.replace(/[TZ]/g, ' ').replace(/\.\d{3}/, '').trim();
      const parts = cleanTime.split(' ');
      if (parts.length >= 2) {
        // 处理日期部分，去掉前导零
        const datePartsArray = parts[0].split('-');
        if (datePartsArray.length === 3) {
          const year = datePartsArray[0];
          const month = parseInt(datePartsArray[1], 10); // 去掉前导零
          const day = parseInt(datePartsArray[2], 10); // 去掉前导零
          const datePart = `${year}/${month}/${day}`;
          const timePart = parts[1];
          return `${datePart} ${timePart}`;
        }
        // 如果解析失败，使用原来的逻辑
        const datePart = parts[0].replace(/-/g, '/');
        const timePart = parts[1];
        return `${datePart} ${timePart}`;
      }
      return timeString;
    } catch (error) {
      return timeString;
    }
  };

  // 显示消息提示
  const showMessage = useCallback((text, type) => {
    if (type === 'error') {
      message.error(text);
    } else if (type === 'success') {
      message.success(text);
    } else if (type === 'warning') {
      message.warning(text);
    } else {
      message.info(text);
    }
  }, []);

  // 获取企业域信息
  const fetchCompanyDomain = useCallback(async () => {
    try {
      const response = await API.get('/api/ram/company-domain');
      setCurrentCompanyDomain(response.data.companyDomain || '');
      
      // 同时获取验证状态
      try {
        const verifyResponse = await API.get('/api/ram/domain-verification-status');
        setIsDomainVerified(verifyResponse.data.isDomainVerified || false);
      } catch (verifyError) {
        // 如果获取验证状态失败，不影响主要功能
        console.error('获取域名验证状态失败:', verifyError);
      }
    } catch (error) {
      console.error('获取企业域信息失败:', error);
    }
  }, []);

  // 统一的错误处理函数
  const handleAPIError = useCallback((error, defaultMessage) => {
    console.error(defaultMessage, error);
    
    // 检查是否为域名验证相关错误
    if (error.response?.data?.needVerification) {
      const { message: errorMsg, currentDomain, isVerified } = error.response.data;
      
      if (currentDomain && !isVerified) {
        showMessage(`${errorMsg}，请先验证企业域 ${currentDomain}`, 'error');
        

      } else {
        showMessage(errorMsg || '请先设置并验证企业域', 'error');
      }
      
      // 刷新企业域信息以更新验证状态
      fetchCompanyDomain();
      return;
    }
    
    // 其他类型的错误
    const errorMessage = error.response?.data?.message || defaultMessage;
    showMessage(errorMessage, 'error');
  }, [showMessage, fetchCompanyDomain]);

  // 获取当前用户信息
  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await API.get('/api/user/profile');
      setCurrentUserRole(response.data.role);
    } catch (error) {
      console.error('获取用户信息失败:', error);
    }
  }, []);

  // 获取RAM账号列表
  const fetchRAMUsers = useCallback(async (page = 1, search = '') => {
    setRamLoading(true);
    try {
      const response = await API.get('/api/ram/list', {
        params: {
          page,
          limit: ramLimit,
          search
        }
      });
      
      // 更新RAM列表数据
      setRamListData({
        ramUsers: response.data.ramUsers || [],
        totalRAMUsers: response.data.totalRAMUsers || 0,
        currentPage: response.data.currentPage || 1,
        totalPages: response.data.totalPages || 1
      });
    } catch (error) {
      handleAPIError(error, '获取RAM账号列表失败');
      setRamListData({
        ramUsers: [],
        totalRAMUsers: 0,
        currentPage: 1,
        totalPages: 1
      });
    }
    setRamLoading(false);
  }, [ramLimit, handleAPIError]);

  // 获取可用IP列表（主账号的IP）- 修改为支持分页和搜索
  const fetchAvailableIPs = useCallback(async (page = 1, search = '') => {
    if (!selectedRAMUser) return;
    
    console.log('fetchAvailableIPs called with:', { 
      ramUserId: selectedRAMUser.id, 
      page, 
      search,
      limit: ipLimit 
    });
    
    setIpLoading(true);
    try {
      const response = await API.get(`/api/ram/available-ips/${selectedRAMUser.id}`, {
        params: {
          page,
          limit: ipLimit,
          search
        }
      });
      
      console.log('fetchAvailableIPs response:', response.data);
      
      setIpListData({
        ips: response.data.availableIps || [],
        totalIps: response.data.totalIps || 0,
        currentPage: response.data.currentPage || 1,
        totalPages: response.data.totalPages || 1
      });
    } catch (error) {
      handleAPIError(error, '获取可用IP失败');
      setIpListData({
        ips: [],
        totalIps: 0,
        currentPage: 1,
        totalPages: 1
      });
    }
    setIpLoading(false);
  }, [selectedRAMUser, ipLimit, handleAPIError]);

  // 获取已分配IP列表 - 新增函数支持分页和搜索
  const fetchAssignedIPs = useCallback(async (page = 1, search = '') => {
    if (!selectedRAMUser) return;
    
    setIpLoading(true);
    try {
      const response = await API.get(`/api/ram/assigned-ips/${selectedRAMUser.id}`, {
        params: {
          page,
          limit: ipLimit,
          search
        }
      });
      
      setIpListData({
        ips: response.data.assignedIps || [],
        totalIps: response.data.totalIps || 0,
        currentPage: response.data.currentPage || 1,
        totalPages: response.data.totalPages || 1
      });
    } catch (error) {
      handleAPIError(error, '获取已分配IP失败');
      setIpListData({
        ips: [],
        totalIps: 0,
        currentPage: 1,
        totalPages: 1
      });
    }
    setIpLoading(false);
  }, [selectedRAMUser, ipLimit, handleAPIError]);

  useEffect(() => {
    if (!permissionLoading && userAccountType === 'main') {
      fetchCurrentUser();
      fetchCompanyDomain();
      fetchRAMUsers(1, ''); // 默认加载第一页数据
    }
  }, [permissionLoading, userAccountType, fetchRAMUsers, fetchCurrentUser, fetchCompanyDomain]);

  // 根据用户角色获取允许的权限
  const getAllowedPermissions = () => {
    if (currentUserRole === 'wlj-user') {
      return {
        canViewIPList: '查看IP',
        canViewBigWallTraffic: '大墙流量',
        canViewAttackRecords: '攻击记录',
        canManageIPs: 'IP策略',
        canExportReports: '导出报告',
        canModifySettings: '账号设置'
      };
    }
    
    // 其他角色（admin, user）可以分配所有权限
    return {
      canViewIPList: '查看IP',
      canViewBigWallTraffic: '大墙流量',
      canViewIPConnections: 'IP连接',
      canViewSmallWallLogs: '小墙流量',
      canViewAttackRecords: '攻击记录',
      canManageIPs: 'IP策略',
      canExportReports: '导出报告',
      canModifySettings: '账号设置'
    };
  };

  // 根据用户角色获取默认权限
  const getDefaultPermissions = () => {
    if (currentUserRole === 'wlj-user') {
      return {
        canViewIPList: true,
        canViewBigWallTraffic: true,
        canViewIPConnections: false,
        canViewSmallWallLogs: false,
        canViewAttackRecords: true,
        canManageIPs: false,
        canExportReports: false,
        canModifySettings: false
      };
    }
    
    // 其他角色的默认权限
    return {
      canViewIPList: true,
      canViewBigWallTraffic: true,
      canViewIPConnections: true,
      canViewSmallWallLogs: true,
      canViewAttackRecords: true,
      canManageIPs: false,
      canExportReports: false,
      canModifySettings: false
    };
  };

  // 创建RAM账号
  const handleCreateRAM = async () => {
    setLoading(true);
    try {
      await API.post('/api/ram/create', {
        username: createForm.username,
        email: createForm.email,
        password: createForm.password,
        qq: createForm.qq,
        allowMultipleLogin: createForm.allowMultipleLogin,
        permissions: createForm.permissions
      });
      showMessage('RAM账号创建成功', 'success');
      fetchRAMUsers(ramListData.currentPage, ramSearchTerm); // 刷新当前页数据
      handleCloseCreateModal();
    } catch (error) {
      handleAPIError(error, '创建RAM账号失败');
    }
    setLoading(false);
  };

  // 修改RAM账号权限
  const handleUpdatePermissions = async () => {
    setLoading(true);
    try {
      await API.put(`/api/ram/permissions/${selectedRAMUser.id}`, {
        permissions: permissionsForm
      });
      showMessage('权限修改成功', 'success');
      fetchRAMUsers(ramListData.currentPage, ramSearchTerm); // 刷新当前页数据
      handleClosePermissionsModal();
    } catch (error) {
      handleAPIError(error, '修改权限失败');
    }
    setLoading(false);
  };

  // 分配或收回IP
  const handleIPAction = async () => {
    setLoading(true);
    try {
      const endpoint = ipAction === 'assign' ? 'assign-ip' : 'revoke-ip';
      const response = await API.post(`/api/ram/${endpoint}/${selectedRAMUser.id}`, {
        ips: selectedIPs
      });
      
      showMessage(response.data.message || `IP${ipAction === 'assign' ? '分配' : '收回'}成功`, 'success');
      fetchRAMUsers(ramListData.currentPage, ramSearchTerm); // 刷新当前页数据
      handleCloseIPModal();
    } catch (error) {
      handleAPIError(error, `${ipAction === 'assign' ? '分配' : '收回'}IP失败`);
    }
    setLoading(false);
  };

  // 切换RAM账号状态
  const handleToggleStatus = async (ramUser) => {
    setLoading(true);
    try {
      const newStatus = ramUser.status === 'active' ? 'disabled' : 'active';
      await API.put(`/api/ram/status/${ramUser.id}`, { status: newStatus });
      showMessage(`账号${newStatus === 'active' ? '启用' : '禁用'}成功`, 'success');
      fetchRAMUsers(ramListData.currentPage, ramSearchTerm); // 刷新当前页数据
    } catch (error) {
      handleAPIError(error, '切换账号状态失败');
    }
    setLoading(false);
  };

  // 删除RAM账号
  const handleDeleteRAM = async () => {
    setLoading(true);
    try {
      await API.delete(`/api/ram/delete/${selectedRAMUser.id}`);
      showMessage('RAM账号删除成功', 'success');
      
      // 计算删除后是否需要调整页码
      const currentPageItemCount = ramListData.ramUsers.length;
      let targetPage = ramListData.currentPage;
      
      // 如果当前页只有一个项目且不是第一页，则跳转到上一页
      if (currentPageItemCount === 1 && ramListData.currentPage > 1) {
        targetPage = ramListData.currentPage - 1;
      }
      
      fetchRAMUsers(targetPage, ramSearchTerm); // 刷新数据
      handleCloseDeleteModal();
    } catch (error) {
      handleAPIError(error, '删除RAM账号失败');
    }
    setLoading(false);
  };

  // 模态框控制函数
  const handleShowCreateModal = async () => {
    try {
      // 先检查RAM创建权限
      const response = await API.get('/api/ram/can-create');
      const { canCreate, accountType, hasPermission, hasCompanyDomain, isDomainVerified, reason } = response.data;
      
      if (!canCreate) {
        if (accountType === 'ram') {
          showMessage('RAM账号无法创建子账号', 'error');
        } else if (!hasPermission) {
          showMessage('您的账号没有RAM创建权限，请联系管理员开启', 'error');
        } else if (!hasCompanyDomain) {
          showMessage('请先设置企业域后再创建RAM账号', 'error');
        } else if (!isDomainVerified) {
          showMessage('请先通过DNS TXT记录验证企业域所有权后再创建RAM账号', 'error');
        } else if (reason) {
          showMessage(reason, 'error');
        } else {
          showMessage('无法创建RAM账号，请联系管理员', 'error');
        }
        return;
      }
      
      // 权限检查通过，显示创建模态框
      setCreateForm({
        username: '',
        email: '',
        password: '',
        qq: '', // 重置QQ号字段
        allowMultipleLogin: false, // 重置多点登录字段
        permissions: getDefaultPermissions()
      });
      setShowCreateModal(true);
    } catch (error) {
      if (error.response?.status === 404) {
        showMessage('用户信息不存在，请重新登录', 'error');
      } else {
        handleAPIError(error, '检查权限失败，请稍后重试');
      }
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  const handleShowPermissionsModal = (ramUser) => {
    setSelectedRAMUser(ramUser);
    setPermissionsForm(ramUser.permissions || {});
    setShowPermissionsModal(true);
  };

  const handleClosePermissionsModal = () => {
    setShowPermissionsModal(false);
    setSelectedRAMUser(null);
  };

  const handleShowIPModal = (ramUser, action) => {
    setSelectedRAMUser(ramUser);
    setIpAction(action);
    setSelectedIPs([]);
    setIpSearchTerm('');
    setIpListData({
      ips: [],
      totalIps: 0,
      currentPage: 1,
      totalPages: 1
    });
    setShowIPModal(true);
  };

  // 监听弹窗打开和RAM用户变化，自动执行初始数据加载
  useEffect(() => {
    if (showIPModal && selectedRAMUser) {
      // 弹窗打开时立即执行一次空搜索，获取第一页数据
      if (ipAction === 'assign') {
        fetchAvailableIPs(1, '');
      } else {
        fetchAssignedIPs(1, '');
      }
    }
  }, [showIPModal, selectedRAMUser, ipAction, fetchAvailableIPs, fetchAssignedIPs]);

  const handleCloseIPModal = () => {
    setShowIPModal(false);
    setSelectedRAMUser(null);
    setSelectedIPs([]);
    setIpSearchTerm('');
    setIpListData({
      ips: [],
      totalIps: 0,
      currentPage: 1,
      totalPages: 1
    });
  };

  // 处理IP搜索
  const handleIPSearch = () => {
    // 移除清空已选择IP的逻辑，保持选择状态
    setIpListData({
      ips: [],
      totalIps: 0,
      currentPage: 1,
      totalPages: 1
    });
    if (ipAction === 'assign') {
      fetchAvailableIPs(1, ipSearchTerm);
    } else {
      fetchAssignedIPs(1, ipSearchTerm);
    }
  };

  // 清空IP搜索
  const handleClearIPSearch = () => {
    setIpSearchTerm('');
    // 移除清空已选择IP的逻辑，保持选择状态
    setIpListData({
      ips: [],
      totalIps: 0,
      currentPage: 1,
      totalPages: 1
    });
    if (ipAction === 'assign') {
      fetchAvailableIPs(1, '');
    } else {
      fetchAssignedIPs(1, '');
    }
  };

  // 处理IP分页
  const handleIPPageChange = (page) => {
    // 移除清空已选择IP的逻辑，保持选择状态
    setIpListData(prev => ({
      ...prev,
      currentPage: page
    }));
    if (ipAction === 'assign') {
      fetchAvailableIPs(page, ipSearchTerm);
    } else {
      fetchAssignedIPs(page, ipSearchTerm);
    }
  };



  const handleShowDeleteModal = (ramUser) => {
    setSelectedRAMUser(ramUser);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedRAMUser(null);
  };

  const handleShowPermissionDetailModal = (ramUser) => {
    setSelectedRAMUserForDetail(ramUser);
    setShowPermissionDetailModal(true);
  };

  const handleClosePermissionDetailModal = () => {
    setShowPermissionDetailModal(false);
    setSelectedRAMUserForDetail(null);
  };

  // 页面切换控制函数
  const handleShowTipsPage = () => {
    setCurrentView('tips');
  };

  const handleBackToMain = () => {
    setCurrentView('main');
  };

  // 企业域管理相关函数
  const handleShowCompanyDomainModal = () => {
    setCompanyDomainForm(currentCompanyDomain);
    
    // 如果有有效的验证记录且令牌未过期，保持在验证步骤
    if (verificationRecord && verificationExpiration) {
      const now = new Date();
      const expiration = new Date(verificationExpiration);
      
      if (now < expiration) {
        setDomainVerificationStep('verification');
      } else {
        // 令牌已过期，重置到输入步骤
        setDomainVerificationStep('input');
        setVerificationRecord('');
        setVerificationExpiration('');
      }
    } else {
      // 没有验证记录，从输入步骤开始
      setDomainVerificationStep('input');
    }
    
    setShowCompanyDomainModal(true);
  };

  const handleCloseCompanyDomainModal = () => {
    setShowCompanyDomainModal(false);
    setCompanyDomainForm('');
    // 注意：不清空验证信息，保持用户的验证状态
    // 只有在验证成功或用户明确要更换域名时才清空
  };

  // 生成域名验证令牌
  const handleGenerateVerificationToken = async () => {
    if (!companyDomainForm.trim()) {
      showMessage('请输入企业域名', 'error');
      return;
    }

    setVerificationLoading(true);
    try {
      const response = await API.post('/api/ram/generate-domain-verification', {
        domain: companyDomainForm.trim()
      });

      const { 
        recordValue, 
        expirationTime 
      } = response.data;

      setVerificationRecord(recordValue);
      setVerificationExpiration(expirationTime);
      setDomainVerificationStep('verification');
      
      showMessage('验证令牌生成成功，请按提示添加DNS记录', 'success');
    } catch (error) {
      console.error('生成验证令牌失败:', error);
      showMessage(error.response?.data?.message || '生成验证令牌失败', 'error');
    }
    setVerificationLoading(false);
  };

  // 验证域名所有权
  const handleVerifyDomainOwnership = async () => {
    setVerificationLoading(true);
    try {
      const response = await API.post('/api/ram/verify-domain-ownership', {
        domain: companyDomainForm.trim()
      });

      showMessage(response.data.message, 'success');
      
      const newDomain = companyDomainForm.trim();
      const isModifying = currentCompanyDomain && currentCompanyDomain !== newDomain;
      
      setIsDomainVerified(true);
      setCurrentCompanyDomain(newDomain);
      
      // 刷新企业域信息
      fetchCompanyDomain();
      
      // 如果是修改企业域，需要刷新RAM账号列表以显示更新后的用户名
      if (isModifying) {
        fetchRAMUsers(ramListData.currentPage, ramSearchTerm);
      }
      
      // 验证成功后清空验证信息
      setVerificationRecord('');
      setVerificationExpiration('');
      setDomainVerificationStep('input');
      
      handleCloseCompanyDomainModal();
    } catch (error) {
      console.error('域名验证失败:', error);
      if (error.response?.data?.instructions) {
        const instructions = error.response.data.instructions;
        showMessage(
          `${error.response.data.message}\n\n` +
          `${instructions.step1}\n` +
          `${instructions.step2}\n` +
          `${instructions.step3}\n` +
          `${instructions.step4}`, 
          'error'
        );
      } else {
        showMessage(error.response?.data?.message || '域名验证失败', 'error');
      }
    }
    setVerificationLoading(false);
  };

  // 返回到域名输入步骤
  const handleBackToInput = () => {
    setDomainVerificationStep('input');
    // 注意：不清空验证记录，让用户可以重新回到验证步骤
  };

  // 设置/修改企业域
  const handleSetCompanyDomain = async () => {
    if (!companyDomainForm.trim()) {
      showMessage('请输入企业域名', 'error');
      return;
    }

    const inputDomain = companyDomainForm.trim();

    // 检查是否与当前域名完全相同且已验证
    if (inputDomain === currentCompanyDomain && isDomainVerified) {
      showMessage('企业域与当前设置相同，无需修改', 'info');
      return;
    }

    // 如果是新域名，先尝试直接设置，让后端检查是否已验证
    setCompanyDomainLoading(true);
    try {
      const response = await API.put('/api/ram/company-domain', {
        domain: inputDomain
      });

      showMessage(response.data.message, 'success');
      
      // 更新当前企业域和验证状态
      setCurrentCompanyDomain(inputDomain);
      setIsDomainVerified(true);
      handleCloseCompanyDomainModal();
      
      // 刷新企业域信息
      fetchCompanyDomain();
      
      // 如果修改成功，刷新RAM账号列表以显示更新后的用户名
      if (response.data.updatedRAMAccounts > 0) {
        fetchRAMUsers(ramListData.currentPage, ramSearchTerm);
      }
    } catch (error) {
      console.error('设置企业域失败:', error);
      
      // 如果后端返回需要验证，走验证流程
      if (error.response?.data?.needVerification) {
        setCompanyDomainLoading(false);
        showMessage('该域名尚未验证，需要先进行验证', 'warning');
        await handleGenerateVerificationToken();
      } else {
        setCompanyDomainLoading(false);
      showMessage(error.response?.data?.message || '设置企业域失败', 'error');
    }
    }
  };

  // 权限描述映射
  const permissionLabels = {
    canViewIPList: '查看IP',
    canViewBigWallTraffic: '大墙流量',
    canViewIPConnections: 'IP连接',
    canViewSmallWallLogs: '小墙流量',
    canViewAttackRecords: '攻击记录',
    canManageIPs: 'IP策略',
    canExportReports: '导出报告',
    canModifySettings: '账号设置'
  };



  // 获取当前用户允许分配的权限
  const allowedPermissions = getAllowedPermissions();

  // 处理RAM搜索
  const handleRAMSearch = () => {
    fetchRAMUsers(1, ramSearchTerm);
  };

  // 清空RAM搜索
  const handleClearRAMSearch = () => {
    setRamSearchTerm('');
    fetchRAMUsers(1, '');
  };

  // 处理RAM分页
  const handleRAMPageChange = (page) => {
    fetchRAMUsers(page, ramSearchTerm);
  };



  // 密码重置相关函数
  const handleShowPasswordModal = (ramUser) => {
    setSelectedRAMUserForPassword(ramUser);
    setNewPasswordForm('');
    setShowPasswordModal(true);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedRAMUserForPassword(null);
    setNewPasswordForm('');
  };

  // 重置RAM账号密码
  const handleResetPassword = async () => {
    if (!newPasswordForm.trim()) {
      showMessage('请输入新密码', 'error');
      return;
    }

    if (newPasswordForm.length < 6) {
      showMessage('密码长度至少为6位', 'error');
      return;
    }

    setPasswordLoading(true);
    try {
      const response = await API.put(`/api/ram/password/${selectedRAMUserForPassword.id}`, {
        newPassword: newPasswordForm.trim()
      });

      showMessage(response.data.message, 'success');
      handleClosePasswordModal();
    } catch (error) {
      handleAPIError(error, '重置密码失败');
    }
    setPasswordLoading(false);
  };

  // 切换RAM账号多点登录权限
  const handleToggleMultiLogin = async (ramUser) => {
    setSelectedRAMUser(ramUser);
    setMultiLoginAction({
      ramUserId: ramUser.id,
      currentPermission: ramUser.allowMultipleLogin,
      username: ramUser.displayName || ramUser.username,
      newPermission: !ramUser.allowMultipleLogin
    });
    setShowMultiLoginModal(true);
  };

  // 确认多点登录权限变更
  const handleConfirmMultiLogin = async () => {
    try {
      setLoading(true);
      const { ramUserId, newPermission, username } = multiLoginAction;
      
      await API.put(`/api/ram/multi-login/${ramUserId}`, {
        allowMultipleLogin: newPermission
      });
      
      showMessage(
        `已${newPermission ? '开启' : '关闭'}RAM账号 ${username} 的多点登录权限`,
        'success'
      );
      
      fetchRAMUsers(ramListData.currentPage, ramSearchTerm);
      setShowMultiLoginModal(false);
      setMultiLoginAction(null);
    } catch (error) {
      handleAPIError(error, '修改多点登录权限失败');
      setShowMultiLoginModal(false);
      setMultiLoginAction(null);
    } finally {
      setLoading(false);
    }
  };

  // 关闭多点登录确认模态框
  const handleCloseMultiLoginModal = () => {
    setShowMultiLoginModal(false);
    setMultiLoginAction(null);
  };

  return (
          <div style={{ padding: '24px', minHeight: 'calc(100vh - 64px)', backgroundColor: '#f0f2f5' }}>
        {/* 权限检查 */}
        {userAccountType !== 'main' ? (
          <PermissionDenied 
            isLoading={permissionLoading}
            permissionName="RAM账号管理"
            userAccountType={userAccountType}
            isAccountTypeRestriction={true}
          />
        ) : (
          <>
            {/* 根据当前视图显示不同内容 */}
            {currentView === 'main' ? (
          <>
            {/* 页面标题和操作按钮 */}
            <Row style={{ marginBottom: '24px' }}>
              <Col span={24}>
                <Card>
                  <div style={{ padding: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <Title level={3} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                          <SettingOutlined style={{ marginRight: '8px' }} />
                          RAM账号管理
                          {currentUserRole === 'wlj-user' && (
                            <Tag color="orange" style={{ marginLeft: '8px' }}>
                              物理机用户模式
                            </Tag>
                          )}
                        </Title>
                        {currentCompanyDomain && (
                          <Text type="secondary" style={{ marginTop: '4px', display: 'block' }}>
                            <BuildOutlined style={{ marginRight: '4px' }} />
                            企业域：<Text type="primary" strong>{currentCompanyDomain}</Text>
                          </Text>
                        )}
                      </div>
                      <Space>
                        <Button 
                          icon={<BuildOutlined />} 
                          onClick={handleShowCompanyDomainModal}
                        >
                          {currentCompanyDomain ? '修改企业域' : '设置企业域'}
                        </Button>
                        <Button 
                          type="dashed"
                          icon={<QuestionCircleOutlined />} 
                          onClick={handleShowTipsPage}
                        >
                          使用提示
                        </Button>
                        <Button 
                          type="primary" 
                          icon={<PlusOutlined />}
                          onClick={handleShowCreateModal}
                        >
                          创建RAM账号
                        </Button>
                      </Space>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>

            {/* 权限限制提示 */}
            {currentUserRole === 'wlj-user' && (
              <Row style={{ marginBottom: '16px' }}>
                <Col span={24}>
                  <Alert 
                    message={
                      <span>
                        <InfoCircleOutlined style={{ marginRight: '8px' }} />
                        <strong>权限限制说明：</strong>作为物理机用户，您只能为RAM账号分配以下权限：查看IP列表、查看IP流量数据（大墙）、查看攻击记录、IP策略管理、导出攻击报告、修改账号信息。
                      </span>
                    }
                    type="info"
                    showIcon
                  />
                </Col>
              </Row>
            )}

                {/* 企业域设置提示 */}
                {!currentCompanyDomain && (
                  <Row style={{ marginBottom: '16px' }}>
                    <Col span={24}>
                      <Alert 
                        message={
                          <div>
                            <ExclamationCircleOutlined style={{ marginRight: '8px' }} />
                            <strong>企业域未设置：</strong>请先设置并验证企业域后才能创建RAM账号。系统通过DNS TXT记录验证域名所有权，确保安全性。
                          </div>
                        }
                        action={
                          <Button 
                            size="small" 
                            type="primary" 
                            ghost
                            onClick={handleShowCompanyDomainModal}
                          >
                            立即设置
                          </Button>
                        }
                        type="warning"
                        showIcon
                      />
                    </Col>
                  </Row>
                )}

                {/* 企业域未验证提示 */}
                {currentCompanyDomain && !isDomainVerified && (
                  <Row style={{ marginBottom: '16px' }}>
                    <Col span={24}>
                      <Alert 
                        message={
                          <div>
                            <SafetyOutlined style={{ marginRight: '8px' }} />
                            <strong>企业域未验证：</strong>您的企业域 <Text code>{currentCompanyDomain}</Text> 尚未通过DNS TXT记录验证。请完成验证后才能创建RAM账号。
                          </div>
                        }
                        action={
                          <Button 
                            size="small" 
                            type="primary" 
                            danger
                            ghost
                            onClick={handleShowCompanyDomainModal}
                          >
                            立即验证
                          </Button>
                        }
                        type="error"
                        showIcon
                      />
                    </Col>
                  </Row>
                )}

            {/* RAM账号列表 */}
            <Row>
              <Col span={24}>
                <Card>
                  <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <Title level={4} style={{ margin: 0 }}>
                        RAM账号列表
                        {ramListData.totalRAMUsers > 0 && (
                          <Tag color="default" style={{ marginLeft: '8px' }}>
                            共 {ramListData.totalRAMUsers} 个
                          </Tag>
                        )}
                      </Title>
                    </div>
                    
                    {/* RAM账号搜索框 */}
                    <div style={{ minWidth: '300px' }}>
                      <Search
                        placeholder="搜索用户名或邮箱"
                        value={ramSearchTerm}
                        onChange={(e) => setRamSearchTerm(e.target.value)}
                        onSearch={handleRAMSearch}
                        onPressEnter={handleRAMSearch}
                        allowClear
                        onClear={handleClearRAMSearch}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                  <div style={{ padding: 0 }}>
                    {ramLoading ? (
                      <div style={{ textAlign: 'center', padding: '48px' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: '16px' }}>加载中...</div>
                      </div>
                    ) : (
                      <>
                        <Table
                          dataSource={ramListData.ramUsers}
                          rowKey="id"
                          pagination={false}
                          locale={{
                            emptyText: ramSearchTerm ? '没有找到匹配的RAM账号' : '暂无RAM账号'
                          }}
                          columns={[
                            {
                              title: '用户名',
                              dataIndex: 'username',
                              key: 'username',
                              render: (text, record) => (
                                <div>
                                  <div style={{ fontWeight: 'bold' }}>
                                    {record.displayName || record.username}
                                  </div>
                                  {record.domain && (
                                    <div style={{ fontSize: '12px', color: '#999' }}>
                                      @{record.domain}
                                    </div>
                                  )}
                                </div>
                              ),
                            },
                            {
                              title: '邮箱',
                              dataIndex: 'email',
                              key: 'email',
                            },
                            {
                              title: '状态',
                              dataIndex: 'status',
                              key: 'status',
                              render: (status) => (
                                <Tag color={status === 'active' ? 'success' : 'error'}>
                                  {status === 'active' ? '启用' : '禁用'}
                                </Tag>
                              ),
                            },
                            {
                              title: '多点登录',
                              dataIndex: 'allowMultipleLogin',
                              key: 'allowMultipleLogin',
                              render: (allowMultipleLogin) => (
                                <Tag color={allowMultipleLogin ? 'success' : 'default'}>
                                  {allowMultipleLogin ? '允许' : '禁止'}
                                </Tag>
                              ),
                            },
                            {
                              title: '分配的IP数量',
                              dataIndex: 'assignedIps',
                              key: 'assignedIps',
                              render: (assignedIps) => assignedIps?.length || 0,
                            },
                            {
                              title: '权限',
                              dataIndex: 'permissions',
                              key: 'permissions',
                              width: 280,
                              render: (permissions) => (
                                <div style={{ 
                                  padding: '2px'
                                }}>
                                  {Object.entries(permissions || {}).map(([key, value]) => {
                                    if (!value || key === '_id') return null;
                                    
                                    const getTagColor = (key) => {
                                      if (key.includes('canView')) return 'blue';
                                      if (key.includes('canManage')) return 'orange';
                                      if (key.includes('canExport')) return 'green';
                                      if (key.includes('canModify')) return 'purple';
                                      return 'default';
                                    };
                                    
                                    return (
                                      <Tag 
                                        key={key} 
                                        color={getTagColor(key)}
                                        size="small"
                                        style={{ margin: '2px' }}
                                      >
                                        {permissionLabels[key]}
                                      </Tag>
                                    );
                                  })}
                                  {(!permissions || Object.entries(permissions).filter(([key, value]) => key !== '_id' && value).length === 0) && (
                                    <Tag icon={<ExclamationCircleOutlined />} color="warning" size="small">
                                      暂无权限
                                    </Tag>
                                  )}
                                </div>
                              ),
                            },
                            {
                              title: '创建时间',
                              dataIndex: 'createdAt',
                              key: 'createdAt',
                              render: (createdAt) => new Date(createdAt).toLocaleString(),
                            },
                            {
                              title: '最后登录',
                              dataIndex: 'lastLoginTime',
                              key: 'lastLoginTime',
                              render: (lastLoginTime) => formatTime(lastLoginTime),
                            },
                            {
                              title: '操作',
                              key: 'actions',
                              width: 350,
                              render: (_, ramUser) => (
                                <Space size="small" wrap>
                                  <Tooltip title="查看权限详情">
                                    <Button
                                      size="small"
                                      icon={<EyeOutlined />}
                                      onClick={() => handleShowPermissionDetailModal(ramUser)}
                                    />
                                  </Tooltip>
                                  <Tooltip title="修改权限">
                                    <Button
                                      size="small"
                                      type="primary"
                                      icon={<KeyOutlined />}
                                      onClick={() => handleShowPermissionsModal(ramUser)}
                                    />
                                  </Tooltip>
                                  <Tooltip title="重置密码">
                                    <Button
                                      size="small"
                                      icon={<LockOutlined />}
                                      onClick={() => handleShowPasswordModal(ramUser)}
                                    />
                                  </Tooltip>
                                  <Tooltip title={ramUser.allowMultipleLogin ? '禁止多点登录' : '允许多点登录'}>
                                    <Button
                                      size="small"
                                      icon={ramUser.allowMultipleLogin ? <LogoutOutlined /> : <LoginOutlined />}
                                      onClick={() => handleToggleMultiLogin(ramUser)}
                                    />
                                  </Tooltip>
                                  <Tooltip title="分配IP">
                                    <Button
                                      size="small"
                                      type="primary"
                                      ghost
                                      icon={<CloudServerOutlined />}
                                      onClick={() => handleShowIPModal(ramUser, 'assign')}
                                    />
                                  </Tooltip>
                                  <Tooltip title="收回IP">
                                    <Button
                                      size="small"
                                      icon={<CloudServerOutlined />}
                                      onClick={() => handleShowIPModal(ramUser, 'revoke')}
                                    />
                                  </Tooltip>
                                  <Tooltip title={ramUser.status === 'active' ? '禁用账号' : '启用账号'}>
                                    <Button
                                      size="small"
                                      icon={ramUser.status === 'active' ? <UserDeleteOutlined /> : <UserAddOutlined />}
                                      onClick={() => handleToggleStatus(ramUser)}
                                    />
                                  </Tooltip>
                                  <Tooltip title="删除账号">
                                    <Button
                                      size="small"
                                      danger
                                      icon={<DeleteOutlined />}
                                      onClick={() => handleShowDeleteModal(ramUser)}
                                    />
                                  </Tooltip>
                                </Space>
                              ),
                            },
                          ]}
                        />

                        {/* 分页组件 */}
                        {ramListData.totalPages > 1 && (
                          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px', padding: '16px' }}>
                            <Pagination
                              current={ramListData.currentPage}
                              total={ramListData.totalRAMUsers}
                              pageSize={ramLimit}
                              showSizeChanger={false}
                              showQuickJumper
                              showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
                              onChange={handleRAMPageChange}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </Card>
              </Col>
            </Row>
              </>
            ) : (
              /* 使用提示页面 */
              <>
                {/* 提示页面标题和返回按钮 */}
                <Row style={{ marginBottom: '24px' }}>
                  <Col span={24}>
                    <Card>
                      <div style={{ padding: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Title level={3} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                            <BookOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                            使用指南
                          </Title>
                          <Space>
                            <Button 
                              icon={<ArrowLeftOutlined />}
                              onClick={handleBackToMain}
                            >
                              返回
                            </Button>
                            <Button 
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={() => {
                                handleBackToMain();
                                handleShowCreateModal();
                              }}
                            >
                              创建RAM账号
                            </Button>
                          </Space>
                        </div>
                      </div>
                    </Card>
                  </Col>
                </Row>

                {/* 提示内容 */}
                <div >
                  {/* 功能介绍 */}
                  <Row style={{ marginBottom: '24px' }}>
                    <Col span={24}>
                      <Card>
                        <div style={{ padding: '24px' }}>
                          <div style={{ marginBottom: '16px' }}>
                            <Title level={4} style={{ color: '#1890ff', marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                              <InfoCircleOutlined style={{ marginRight: '8px' }} />
                              什么是RAM账号管理
                            </Title>
                            <Paragraph style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '16px' }}>
                              RAM账号管理是用于创建和管理子账号的功能，允许主账号用户为不同的用户或团队分配特定的权限和IP资源。
                            </Paragraph>
                          </div>
                          <Row gutter={16}>
                            <Col md={6}>
                              <div style={{ textAlign: 'center', padding: '16px' }}>
                                <CrownOutlined style={{ fontSize: '32px', color: '#faad14', marginBottom: '8px', display: 'block' }} />
                                <Title level={5}>主账号专属</Title>
                                <Text type="secondary">只有主账号用户才能使用</Text>
                              </div>
                            </Col>
                            <Col md={6}>
                              <div style={{ textAlign: 'center', padding: '16px' }}>
                                <BuildOutlined style={{ fontSize: '32px', color: '#1890ff', marginBottom: '8px', display: 'block' }} />
                                <Title level={5}>企业域管理</Title>
                                <Text type="secondary">统一的企业域身份标识</Text>
                              </div>
                            </Col>
                            <Col md={6}>
                              <div style={{ textAlign: 'center', padding: '16px' }}>
                                <GlobalOutlined style={{ fontSize: '32px', color: '#52c41a', marginBottom: '8px', display: 'block' }} />
                                <Title level={5}>IP资源分配</Title>
                                <Text type="secondary">灵活分配IP资源</Text>
                              </div>
                            </Col>
                            <Col md={6}>
                              <div style={{ textAlign: 'center', padding: '16px' }}>
                                <SettingOutlined style={{ fontSize: '32px', color: '#8c8c8c', marginBottom: '8px', display: 'block' }} />
                                <Title level={5}>实时管理</Title>
                                <Text type="secondary">支持实时启用和禁用</Text>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </Card>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    {/* 创建账号 */}
                    <Col span={12}>
                      <Card style={{ height: '100%' }}>
                        <div style={{ padding: '16px' }}>
                          <Title level={5} style={{ marginBottom: '16px' }}>
                            <UserAddOutlined style={{ marginRight: '8px' }} />
                            创建RAM账号
                          </Title>
                          
                          <div style={{ marginBottom: '16px' }}>
                            <Title level={5}>操作步骤</Title>
                            <ol style={{ paddingLeft: '20px' }}>
                              <li>点击"创建RAM账号"按钮</li>
                              <li>填写基本信息（用户名、邮箱、密码）</li>
                              <li>可选填写QQ号</li>
                              <li>设置初始权限</li>
                              <li>确认创建</li>
                            </ol>
                          </div>
                          
                          <div>
                            <Title level={5}>注意事项</Title>
                            <ul style={{ paddingLeft: '20px' }}>
                              <li>用户名只需输入显示名称</li>
                              <li>系统会自动添加企业域后缀</li>
                              <li>邮箱用于登录验证</li>
                              <li>建议设置强密码</li>
                              <li>权限可以后续调整</li>
                              <li>多点登录默认关闭，可按需开启</li>
                            </ul>
                          </div>
                        </div>
                      </Card>
                    </Col>
                    
                    {/* 企业域管理 */}
                    <Col span={12}>
                      <Card style={{ height: '100%' }}>
                        <div style={{ padding: '16px' }}>
                          <Title level={5} style={{ marginBottom: '16px' }}>
                            <BuildOutlined style={{ marginRight: '8px' }} />
                            企业域管理
                          </Title>
                          
                          <div style={{ marginBottom: '16px' }}>
                            <Title level={5}>企业域功能</Title>
                            <ul style={{ paddingLeft: '20px' }}>
                              <li>所有RAM账号统一企业身份</li>
                              <li>完整用户名格式：用户名@企业域</li>
                              <li>通过DNS TXT记录验证域名所有权</li>
                              <li>确保企业内部账号的安全性和统一性</li>
                            </ul>
                          </div>
                          
                          <div>
                            <Title level={5}>验证流程</Title>
                            <ol style={{ paddingLeft: '20px' }}>
                              <li>输入企业域名（如：company.com）</li>
                              <li>系统生成DNS TXT验证记录</li>
                              <li>在DNS管理控制台添加TXT记录</li>
                              <li>等待DNS记录生效（5-60分钟）</li>
                              <li>点击"验证域名"完成验证</li>
                              <li>验证成功后即可创建RAM账号</li>
                            </ol>
                          </div>
                        </div>
                      </Card>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    {/* 权限管理 */}
                    <Col span={24}>
                      <Card>
                        <div style={{ padding: '16px' }}>
                          <Title level={5} style={{ marginBottom: '16px' }}>
                            <KeyOutlined style={{ marginRight: '8px' }} />
                            权限管理
                          </Title>
                          
                          <Row gutter={[16, 16]}>
                            <Col span={12}>
                              <div style={{ marginBottom: '16px' }}>
                                <Title level={5}>可分配权限</Title>
                                <div style={{ marginBottom: '16px' }}>
                                  <Tag color="blue" style={{ margin: '2px' }}>查看IP</Tag>
                                  <Tag color="blue" style={{ margin: '2px' }}>大墙流量</Tag>
                                  <Tag color="blue" style={{ margin: '2px' }}>IP连接</Tag>
                                  <Tag color="blue" style={{ margin: '2px' }}>小墙流量</Tag>
                                </div>
                                <div style={{ marginBottom: '16px' }}>
                                  <Tag color="blue" style={{ margin: '2px' }}>攻击记录</Tag>
                                  <Tag color="orange" style={{ margin: '2px' }}>IP策略</Tag>
                                  <Tag color="green" style={{ margin: '2px' }}>导出报告</Tag>
                                  <Tag color="default" style={{ margin: '2px' }}>账号设置</Tag>
                                </div>
                              </div>

                              <div>
                                <Title level={5}>权限说明</Title>
                                <ul style={{ paddingLeft: '20px' }}>
                                  <li><Text strong>大墙流量、攻击记录、IP策略：</Text>适用于所有IP类型</li>
                                  <li><Text strong>IP连接、小墙流量：</Text>仅适用于云服务器/宿主机/托管IP</li>
                                  <li><Text strong>查看IP、导出报告、账号设置：</Text>通用功能权限</li>
                                </ul>
                              </div>
                            </Col>
                            
                            <Col span={12}>
                              <div style={{ backgroundColor: '#f8f9fa', padding: '16px', borderRadius: '6px', marginBottom: '16px' }}>
                                <Title level={5} style={{ marginBottom: '8px' }}>
                                  <ExclamationCircleOutlined style={{ marginRight: '8px' }} />
                                  重要提示
                                </Title>
                                <Paragraph style={{ marginBottom: '8px' }}>
                                  如果要分配的IP为<Text strong>物理机IP</Text>，请不要给RAM账号分配"小墙流量"、"IP连接"权限，
                                  因为物理机IP不支持这些功能，即使分配了也无法正常使用。
                                </Paragraph>
                                <Paragraph style={{ margin: 0 }}>
                                  <Text strong>域名验证安全性：</Text>系统通过DNS TXT记录验证企业域所有权，确保只有域名真正的所有者才能使用该域名作为企业域，提高账号安全性。
                                </Paragraph>
                              </div>
                              
                              {currentUserRole === 'wlj-user' && (
                                <div style={{ backgroundColor: '#fff2e8', padding: '16px', borderRadius: '6px', border: '1px solid #ffd591' }}>
                                  <Title level={5} style={{ marginBottom: '8px', color: '#fa8c16' }}>
                                    <ExclamationCircleOutlined style={{ marginRight: '8px' }} />
                                    权限限制
                                  </Title>
                                  <Paragraph style={{ margin: 0 }}>物理机用户只能分配基础权限，不包括IP连接和小墙流量权限</Paragraph>
                                </div>
                              )}
                            </Col>
                          </Row>
                        </div>
                      </Card>
                    </Col>
                  </Row>

                  {/* IP资源管理 */}
                  <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col span={24}>
                      <Card>
                        <div style={{ padding: '16px' }}>
                          <Title level={5} style={{ marginBottom: '16px' }}>
                            <CloudServerOutlined style={{ marginRight: '8px' }} />
                            IP资源管理
                          </Title>
                          <Row gutter={[16, 16]}>
                            <Col span={12}>
                              <div style={{ marginBottom: '16px' }}>
                                <Title level={5}>
                                  <PlusCircleOutlined style={{ marginRight: '8px' }} />
                                  分配IP
                                </Title>
                                <ul style={{ paddingLeft: '20px' }}>
                                  <li>点击绿色服务器图标</li>
                                  <li>搜索查找目标IP</li>
                                  <li>支持批量选择</li>
                                  <li>确认后立即生效</li>
                                </ul>
                              </div>
                            </Col>
                            <Col span={12}>
                              <div style={{ marginBottom: '16px' }}>
                                <Title level={5}>
                                  <MinusCircleOutlined style={{ marginRight: '8px' }} />
                                  收回IP
                                </Title>
                                <ul style={{ paddingLeft: '20px' }}>
                                  <li>点击橙色服务器图标</li>
                                  <li>查看已分配的IP</li>
                                  <li>选择需要收回的IP</li>
                                  <li>IP将被取消共享</li>
                                </ul>
                              </div>
                            </Col>
                          </Row>
                          <div style={{ backgroundColor: '#f6ffed', padding: '16px', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
                            <Paragraph style={{ margin: 0 }}>
                              <BulbOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
                              <Text strong>提示：</Text>IP分配是实时生效的，RAM账号分配到IP后立即可以查看和使用。
                            </Paragraph>
                          </div>
                        </div>
                      </Card>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    {/* 安全建议 */}
                    <Col span={12}>
                      <Card style={{ height: '100%' }}>
                        <div style={{ padding: '16px' }}>
                          <Title level={5} style={{ marginBottom: '16px' }}>
                            <SafetyOutlined style={{ marginRight: '8px' }} />
                            安全建议
                          </Title>
                          
                          <div style={{ marginBottom: '16px' }}>
                            <Title level={5}>权限管理</Title>
                            <ul style={{ paddingLeft: '20px' }}>
                              <li>只分配必要权限</li>
                              <li>定期检查权限设置</li>
                              <li>及时禁用闲置账号</li>
                              <li>谨慎分配管理权限</li>
                            </ul>
                          </div>

                          <div style={{ marginBottom: '16px' }}>
                            <Title level={5}>多点登录管理</Title>
                            <ul style={{ paddingLeft: '20px' }}>
                              <li>新账号默认禁止多点登录</li>
                              <li>根据实际需求开启多点登录</li>
                              <li>团队协作账号可考虑开启</li>
                              <li>高敏感账号建议保持单点登录</li>
                              <li>定期审查多点登录权限</li>
                            </ul>
                          </div>

                          <div style={{ marginBottom: '16px' }}>
                            <Title level={5}>密码安全</Title>
                            <ul style={{ paddingLeft: '20px' }}>
                              <li>定期重置RAM账号密码</li>
                              <li>使用强密码策略</li>
                              <li>安全传达密码给用户</li>
                              <li>监控异常登录活动</li>
                              <li>重置密码会静默强制在线账号退出</li>
                            </ul>
                          </div>

                          <div>
                            <Title level={5}>账号监控</Title>
                            <ul style={{ paddingLeft: '20px' }}>
                              <li>关注异常登录时间</li>
                              <li>监控IP分配使用情况</li>
                              <li>定期检查账号活跃度</li>
                              <li>及时处理安全事件</li>
                            </ul>
                          </div>
                        </div>
                      </Card>
                    </Col>
                    
                    {/* 操作技巧 */}
                    <Col span={12}>
                      <Card style={{ height: '100%' }}>
                        <div style={{ padding: '16px' }}>
                          <Title level={5} style={{ marginBottom: '16px' }}>
                            <BulbOutlined style={{ marginRight: '8px' }} />
                            操作技巧
                          </Title>
                          
                          <div style={{ marginBottom: '16px' }}>
                            <Title level={5}>搜索功能</Title>
                            <ul style={{ paddingLeft: '20px' }}>
                              <li>支持用户名和邮箱搜索</li>
                              <li>IP分配支持前缀搜索</li>
                            </ul>
                          </div>
                          
                          <div style={{ marginBottom: '16px' }}>
                            <Title level={5}>批量操作</Title>
                            <ul style={{ paddingLeft: '20px' }}>
                              <li>IP分配支持多选</li>
                              <li>提高操作效率</li>
                            </ul>
                          </div>
                          
                          <div>
                            <Title level={5}>状态管理</Title>
                            <ul style={{ paddingLeft: '20px' }}>
                              <li>禁用比删除更安全</li>
                              <li>删除会回收所有IP</li>
                              <li>多点登录可随时切换</li>
                              <li>关闭多点登录会踢出多余会话</li>
                            </ul>
                          </div>
                        </div>
                      </Card>
                    </Col>
                  </Row>
                </div>
              </>
            )}

            {/* 创建RAM账号模态框 */}
            <Modal 
              title="创建RAM账号" 
              open={showCreateModal} 
              onCancel={handleCloseCreateModal}
              width={800}
              footer={[
                <Button key="cancel" onClick={handleCloseCreateModal}>
                  取消
                </Button>,
                <Button 
                  key="create" 
                  type="primary" 
                  loading={loading}
                  onClick={handleCreateRAM}
                >
                  创建
                </Button>
              ]}
            >
              <Form layout="vertical">
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="用户名">
                      <Input
                        placeholder="输入用户名"
                        value={createForm.username}
                        onChange={(e) => setCreateForm({
                          ...createForm,
                          username: e.target.value
                        })}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="邮箱">
                      <Input
                        type="email"
                        placeholder="输入邮箱"
                        value={createForm.email}
                        onChange={(e) => setCreateForm({
                          ...createForm,
                          email: e.target.value
                        })}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="密码">
                      <Input.Password
                        placeholder="输入密码"
                        value={createForm.password}
                        onChange={(e) => setCreateForm({
                          ...createForm,
                          password: e.target.value
                        })}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={<span>QQ号 <Text type="secondary">(可选)</Text></span>}>
                      <Input
                        placeholder="输入QQ号"
                        value={createForm.qq}
                        onChange={(e) => setCreateForm({
                          ...createForm,
                          qq: e.target.value
                        })}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col span={24}>
                    <Form.Item>
                      <Switch
                        checked={createForm.allowMultipleLogin}
                        onChange={(checked) => setCreateForm({
                          ...createForm,
                          allowMultipleLogin: checked
                        })}
                        style={{ marginRight: '8px' }}
                      />
                      <span>允许多点登录</span>
                      <div style={{ color: '#8c8c8c', fontSize: '12px', marginTop: '4px' }}>
                        开启后，该RAM账号可以在多个设备上同时保持登录状态，不会被互相踢下线。
                      </div>
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item 
                  label={
                    <span>
                      权限设置
                      {currentUserRole === 'wlj-user' && (
                        <Tag color="orange" style={{ marginLeft: '8px' }}>
                          受限模式
                        </Tag>
                      )}
                    </span>
                  }
                >
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                    {Object.entries(allowedPermissions).map(([key, label]) => (
                      <Checkbox
                        key={key}
                        checked={createForm.permissions[key] || false}
                        onChange={(e) => setCreateForm({
                          ...createForm,
                          permissions: {
                            ...createForm.permissions,
                            [key]: e.target.checked
                          }
                        })}
                      >
                        {label}
                      </Checkbox>
                    ))}
                  </div>
                </Form.Item>
              </Form>
            </Modal>

            {/* 修改权限模态框 */}
            <Modal 
              title={
                <span>
                  修改权限 - {selectedRAMUser?.displayName || selectedRAMUser?.username}
                  {selectedRAMUser?.domain && (
                    <Text type="secondary" style={{ marginLeft: '4px' }}>@{selectedRAMUser.domain}</Text>
                  )}
                  {currentUserRole === 'wlj-user' && (
                    <Tag color="orange" style={{ marginLeft: '8px' }}>
                      受限模式
                    </Tag>
                  )}
                </span>
              }
              open={showPermissionsModal} 
              onCancel={handleClosePermissionsModal}
              footer={[
                <Button key="cancel" onClick={handleClosePermissionsModal}>
                  取消
                </Button>,
                <Button 
                  key="save" 
                  type="primary" 
                  loading={loading}
                  onClick={handleUpdatePermissions}
                >
                  保存
                </Button>
              ]}
            >
              <Form layout="vertical">
                <Form.Item label="权限设置">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                    {Object.entries(allowedPermissions).map(([key, label]) => (
                      <Checkbox
                        key={key}
                        checked={permissionsForm[key] || false}
                        onChange={(e) => setPermissionsForm({
                          ...permissionsForm,
                          [key]: e.target.checked
                        })}
                      >
                        {label}
                      </Checkbox>
                    ))}
                  </div>
                </Form.Item>
              </Form>
            </Modal>

            {/* IP分配/收回模态框 */}
            <Modal 
              title={
                <span>
                  {ipAction === 'assign' ? '分配IP' : '收回IP'} - {selectedRAMUser?.displayName || selectedRAMUser?.username}
                  {selectedRAMUser?.domain && (
                    <Text type="secondary" style={{ marginLeft: '4px' }}>@{selectedRAMUser.domain}</Text>
                  )}
                </span>
              }
              open={showIPModal} 
              onCancel={handleCloseIPModal}
              width={800}
              footer={[
                <Button key="cancel" onClick={handleCloseIPModal}>
                  取消
                </Button>,
                <Button 
                  key="action"
                  type={ipAction === 'assign' ? 'primary' : 'default'}
                  loading={loading}
                  disabled={selectedIPs.length === 0}
                  onClick={handleIPAction}
                >
                  {ipAction === 'assign' ? `分配 (${selectedIPs.length})` : `收回 (${selectedIPs.length})`}
                </Button>
              ]}
            >
              <Form layout="vertical">
                {/* IP搜索框 */}
                <Form.Item label="搜索IP地址">
                  <Search
                    placeholder="输入IP地址前缀，如 192.168."
                    value={ipSearchTerm}
                    onChange={(e) => setIpSearchTerm(e.target.value)}
                    onSearch={handleIPSearch}
                    onPressEnter={handleIPSearch}
                    allowClear
                    onClear={handleClearIPSearch}
                  />
                </Form.Item>

                <Form.Item 
                  label={
                    <span>
                      {ipAction === 'assign' ? '选择要分配的IP' : '选择要收回的IP'}
                      {ipListData.totalIps > 0 && (
                        <Tag color="blue" style={{ marginLeft: '8px' }}>
                          共 {ipListData.totalIps} 个IP
                        </Tag>
                      )}
                    </span>
                  }
                >
                  {ipLoading ? (
                    <div style={{ textAlign: 'center', padding: '24px' }}>
                      <Spin />
                      <span style={{ marginLeft: '8px' }}>加载中...</span>
                    </div>
                  ) : (
                    <>
                      <div style={{ maxHeight: '380px', overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: '6px', padding: '16px' }}>
                        {ipListData.ips.length === 0 ? (
                          <Alert 
                            message={
                              ipAction === 'assign' 
                                ? (ipSearchTerm ? '没有匹配的可分配IP' : '没有可分配的IP')
                                : (ipSearchTerm ? '没有匹配的已分配IP' : '该用户没有已分配的IP')
                            }
                            type="info"
                            showIcon
                          />
                        ) : (
                          <Checkbox.Group 
                            value={selectedIPs}
                            onChange={(checkedValues) => setSelectedIPs(checkedValues)}
                            style={{ width: '100%' }}
                          >
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                              {ipListData.ips.map((ip) => (
                                <Checkbox key={ip} value={ip}>
                                  {ip}
                                </Checkbox>
                              ))}
                            </div>
                          </Checkbox.Group>
                        )}
                      </div>

                      {/* 分页组件 */}
                      {ipListData.totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                          <Pagination
                            size="small"
                            current={ipListData.currentPage}
                            total={ipListData.totalIps}
                            pageSize={ipLimit}
                            showSizeChanger={false}
                            showQuickJumper
                            showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
                            onChange={handleIPPageChange}
                          />
                        </div>
                      )}

                      {/* 已选择IP提示 */}
                      {selectedIPs.length > 0 && (
                        <Alert 
                          style={{ marginTop: '16px' }}
                          message={
                            <div>
                              <Text strong>已选择 {selectedIPs.length} 个IP：</Text>
                              <div style={{ marginTop: '8px' }}>
                                {selectedIPs.map(ip => (
                                  <Tag key={ip} color="blue" style={{ margin: '2px' }}>
                                    {ip}
                                  </Tag>
                                ))}
                              </div>
                            </div>
                          }
                          type="success"
                          showIcon
                        />
                      )}
                    </>
                  )}
                </Form.Item>
              </Form>
            </Modal>

            {/* 删除确认模态框 */}
            <Modal 
              title="确认删除" 
              open={showDeleteModal} 
              onCancel={handleCloseDeleteModal}
              footer={[
                <Button key="cancel" onClick={handleCloseDeleteModal}>
                  取消
                </Button>,
                <Button 
                  key="delete" 
                  type="primary" 
                  danger
                  loading={loading}
                  onClick={handleDeleteRAM}
                >
                  确认删除
                </Button>
              ]}
            >
              <div>
                <Paragraph>确定要删除RAM账号 <Text strong>{selectedRAMUser?.displayName || selectedRAMUser?.username}</Text> 吗？</Paragraph>
                {selectedRAMUser?.domain && (
                  <Paragraph type="secondary" style={{ fontSize: '12px' }}>企业域：{selectedRAMUser.domain}</Paragraph>
                )}
                <Paragraph type="danger">此操作不可撤销，该账号的所有IP权限将被收回。</Paragraph>
              </div>
            </Modal>

            {/* 权限详情查看模态框 */}
            <Modal 
              title={
                <span>
                  <SafetyOutlined style={{ marginRight: '8px' }} />
                  权限详情 - {selectedRAMUserForDetail?.displayName || selectedRAMUserForDetail?.username}
                  {selectedRAMUserForDetail?.domain && (
                    <Text type="secondary" style={{ marginLeft: '4px' }}>@{selectedRAMUserForDetail.domain}</Text>
                  )}
                </span>
              }
              open={showPermissionDetailModal} 
              onCancel={handleClosePermissionDetailModal}
              width={1000}
              footer={[
                <Button key="close" onClick={handleClosePermissionDetailModal}>
                  关闭
                </Button>,
                <Button 
                  key="edit" 
                  type="primary"
                  onClick={() => {
                    handleClosePermissionDetailModal();
                    handleShowPermissionsModal(selectedRAMUserForDetail);
                  }}
                >
                  修改权限
                </Button>
              ]}
            >
              <div>
                {selectedRAMUserForDetail && (
                  <div>
                    <Card style={{ marginBottom: '16px' }}>
                      <div style={{ marginBottom: '16px' }}>
                        <Title level={5}>基本信息</Title>
                      </div>
                      <Row gutter={[16, 8]}>
                        <Col span={6}>
                          <Paragraph><Text strong>用户名：</Text> {selectedRAMUserForDetail.displayName || selectedRAMUserForDetail.username}</Paragraph>
                          {selectedRAMUserForDetail.domain && (
                            <Paragraph><Text strong>企业域：</Text> {selectedRAMUserForDetail.domain}</Paragraph>
                          )}
                        </Col>
                        <Col span={6}>
                          <Paragraph><Text strong>邮箱：</Text> {selectedRAMUserForDetail.email}</Paragraph>
                        </Col>
                        <Col span={6}>
                          <Paragraph>
                            <Text strong>状态：</Text> 
                            <Tag color={selectedRAMUserForDetail.status === 'active' ? 'success' : 'error'} style={{ marginLeft: '8px' }}>
                              {selectedRAMUserForDetail.status === 'active' ? '启用' : '禁用'}
                            </Tag>
                          </Paragraph>
                        </Col>
                        <Col span={6}>
                          <Paragraph><Text strong>分配IP数量：</Text> {selectedRAMUserForDetail.assignedIps?.length || 0}</Paragraph>
                        </Col>
                      </Row>
                    </Card>

                    <Card style={{ marginBottom: '16px' }}>
                      <div style={{ marginBottom: '16px' }}>
                        <Title level={5}>权限详情</Title>
                      </div>
                      <div>
                        {(!selectedRAMUserForDetail.permissions || Object.entries(selectedRAMUserForDetail.permissions).filter(([key, value]) => key !== '_id' && value).length === 0) ? (
                          <Alert 
                            type="warning" 
                            style={{ textAlign: 'center' }}
                            message={
                              <div>
                                <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
                                  <ExclamationCircleOutlined style={{ marginRight: '8px' }} />
                                  该RAM账号权限为空
                                </Title>
                                <Paragraph style={{ margin: 0 }}>
                                  当前账号没有任何权限，无法执行系统操作。请点击"修改权限"按钮为账号分配相应权限。
                                </Paragraph>
                              </div>
                            }
                          />
                        ) : (
                          <Row gutter={[16, 8]}>
                            {Object.entries(permissionLabels).map(([key, label]) => (
                              <Col span={6} key={key}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                  {selectedRAMUserForDetail.permissions?.[key] ? (
                                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                                  ) : (
                                    <CloseOutlined style={{ color: '#ff4d4f', marginRight: '8px' }} />
                                  )}
                                  <Text style={{ color: selectedRAMUserForDetail.permissions?.[key] ? '#52c41a' : '#8c8c8c' }}>
                                    {label}
                                  </Text>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        )}
                      </div>
                    </Card>

                    <Card style={{ marginBottom: '16px' }}>
                      <div style={{ marginBottom: '16px' }}>
                        <Title level={5}>权限说明</Title>
                      </div>
                      <div>
                        {(!selectedRAMUserForDetail.permissions || Object.entries(selectedRAMUserForDetail.permissions).filter(([key, value]) => key !== '_id' && value).length === 0) ? (
                          <Alert 
                            type="info"
                            message={
                              <div>
                                <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
                                  <InfoCircleOutlined style={{ marginRight: '8px' }} />
                                  如何为RAM账号分配权限
                                </Title>
                                <ol style={{ margin: 0, paddingLeft: '20px' }}>
                                  <li>点击下方的"修改权限"按钮</li>
                                  <li>在弹出的权限设置对话框中勾选需要的权限</li>
                                  <li>点击"保存"完成权限分配</li>
                                  <li>权限生效后，该RAM账号即可使用相应功能</li>
                                </ol>
                              </div>
                            }
                          />
                        ) : (
                          <Row gutter={[16, 0]}>
                            <Col span={12}>
                              <ul style={{ paddingLeft: '20px' }}>
                                <li><Text strong>查看IP</Text>：查看分配给账号的IP地址列表</li>
                                <li><Text strong>大墙流量</Text>：查看大墙流量监控数据</li>
                                <li><Text strong>IP连接</Text>：查看IP连接数和网络状态</li>
                                <li><Text strong>小墙流量</Text>：查看小墙流量监控数据</li>
                              </ul>
                            </Col>
                            <Col span={12}>
                              <ul style={{ paddingLeft: '20px' }}>
                                <li><Text strong>攻击记录</Text>：查看IP攻击日志和统计信息</li>
                                <li><Text strong>IP策略</Text>：管理IP策略和规则</li>
                                <li><Text strong>导出报告</Text>：导出攻击日志PDF报告</li>
                                <li><Text strong>账号设置</Text>：修改密码、邮箱等</li>
                              </ul>
                            </Col>
                          </Row>
                        )}
                      </div>
                    </Card>
                  </div>
                )}
              </div>

            </Modal>

            {/* 企业域设置模态框 */}
            <Modal 
              title={
                <span>
                  <BuildOutlined style={{ marginRight: '8px' }} />
                  {currentCompanyDomain ? '修改企业域' : '设置企业域'}
                  {isDomainVerified && (
                    <Tag color="success" style={{ marginLeft: '8px' }}>
                      <CheckCircleOutlined />
                      已验证
                    </Tag>
                  )}
                </span>
              }
              open={showCompanyDomainModal} 
              onCancel={handleCloseCompanyDomainModal}
              width={800}
              footer={[
                <Button key="cancel" onClick={handleCloseCompanyDomainModal}>
                  取消
                </Button>,
                
                ...(domainVerificationStep === 'input' ? [
                  <Button 
                    key="set"
                    type="primary" 
                    onClick={() => {
                      const inputDomain = companyDomainForm.trim();
                      // 如果是相同域名但未验证，调用验证流程
                      if (inputDomain === currentCompanyDomain && !isDomainVerified) {
                        handleGenerateVerificationToken();
                      } else {
                        // 其他情况调用设置API
                        handleSetCompanyDomain();
                      }
                    }}
                    disabled={companyDomainLoading || verificationLoading}
                  >
                    {(companyDomainLoading || verificationLoading) ? (
                      <>
                        <Spin size="small" style={{ marginRight: '8px' }} />
                        {companyDomainLoading ? '设置中...' : '生成验证令牌...'}
                      </>
                    ) : (
                      <>
                        {companyDomainForm === currentCompanyDomain && isDomainVerified ? '确认设置' : 
                         companyDomainForm === currentCompanyDomain && !isDomainVerified ? '验证域名' :
                         currentCompanyDomain ? '修改企业域' : '设置企业域'}
                      </>
                    )}
                  </Button>
                ] : []),

                ...(domainVerificationStep === 'verification' ? [
                  <Button 
                    key="back" 
                    onClick={handleBackToInput}
                    icon={<ArrowLeftOutlined />}
                  >
                    返回
                  </Button>,
                  <Button 
                    key="verify"
                    type="primary" 
                    onClick={handleVerifyDomainOwnership}
                    disabled={verificationLoading}
                    icon={verificationLoading ? <Spin size="small" /> : <CheckCircleOutlined />}
                  >
                    {verificationLoading ? '验证中...' : '验证域名'}
                  </Button>
                ] : [])
              ]}
            >
              <div>
                {/* 域名输入步骤 */}
                {domainVerificationStep === 'input' && (
                  <div>
                    <div style={{ marginBottom: '24px' }}>
                      <Text strong style={{ display: 'block', marginBottom: '8px' }}>企业域名</Text>
                      <Input
                        placeholder="例如：company.com"
                        value={companyDomainForm}
                        onChange={(e) => {
                          const newDomain = e.target.value;
                          setCompanyDomainForm(newDomain);
                          
                          // 如果用户更换了域名，清空之前的验证信息
                          if (verificationRecord && newDomain.trim() !== currentCompanyDomain) {
                            setVerificationRecord('');
                            setVerificationExpiration('');
                          }
                        }}
                      />
                      <Text type="secondary" style={{ display: 'block', marginTop: '4px' }}>
                        企业域将作为所有RAM账号用户名的后缀，格式为：用户名@企业域
                      </Text>
                    </div>

                    {currentCompanyDomain && (
                      <Alert 
                        type={isDomainVerified ? 'info' : 'warning'}
                        style={{ marginBottom: '16px' }}
                        message={
                          <div>
                            <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
                              <InfoCircleOutlined style={{ marginRight: '8px' }} />
                              当前企业域信息
                            </Title>
                            <Paragraph style={{ marginBottom: '8px' }}>
                              当前企业域：<Text code>{currentCompanyDomain}</Text>
                            </Paragraph>
                            <Paragraph style={{ marginBottom: companyDomainForm && companyDomainForm !== currentCompanyDomain ? '8px' : 0 }}>
                              状态：
                              <Tag color={isDomainVerified ? 'success' : 'warning'} style={{ marginLeft: '4px' }}>
                                {isDomainVerified ? '已验证' : '未验证'}
                              </Tag>
                              {!isDomainVerified && (
                                <span style={{ marginLeft: '4px' }}>
                                  （需要重新验证才能使用）
                                </span>
                              )}
                            </Paragraph>
                            {companyDomainForm && companyDomainForm !== currentCompanyDomain && (
                              <Paragraph style={{ margin: 0 }}>
                                <ArrowRightOutlined style={{ marginRight: '4px' }} />
                                将要修改为：<Text code>{companyDomainForm}</Text>
                              </Paragraph>
                            )}
                          </div>
                        }
                      />
                    )}

                    <Alert 
                      type="warning"
                      message={
                        <div>
                          <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
                            <SafetyOutlined style={{ marginRight: '8px' }} />
                            域名验证要求
                          </Title>
                          <Paragraph style={{ marginBottom: '8px' }}>
                            为确保企业域的安全性，系统需要通过DNS TXT记录验证您对该域名的所有权。
                          </Paragraph>
                          <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            <li>只有经过验证的域名才能用作企业域</li>
                            <li>验证过程需要在您的DNS管理控制台添加指定的TXT记录</li>
                            <li>验证令牌有效期为24小时</li>
                          </ul>
                        </div>
                      }
                    />
                  </div>
                )}

                {/* DNS验证步骤 */}
                {domainVerificationStep === 'verification' && (
                  <div>
                    <Alert 
                      type="info"
                      style={{ marginBottom: '16px' }}
                      message={
                        <div>
                          <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
                            <GlobalOutlined style={{ marginRight: '8px' }} />
                            DNS TXT记录配置
                          </Title>
                          <Paragraph style={{ margin: 0 }}>
                            请在您的DNS管理控制台中添加以下TXT记录：
                          </Paragraph>
                        </div>
                      }
                    />

                    <Card style={{ marginBottom: '16px' }}>
                      <div style={{ marginBottom: '16px' }}>
                        <Title level={5}>DNS记录信息</Title>
                      </div>
                      <div>
                        <Row gutter={[16, 8]}>
                          <Col span={6}>
                            <Text strong>记录类型：</Text>
                          </Col>
                          <Col span={18}>
                            <Text code>TXT</Text>
                          </Col>
                        </Row>
                        <Row gutter={[16, 8]}>
                          <Col span={6}>
                            <Text strong>记录名称：</Text>
                          </Col>
                          <Col span={18}>
                            <Space>
                              <Text code>_ddos_verification</Text>
                              <Button 
                                type="default" 
                                size="small" 
                                icon={<CopyOutlined />}
                                onClick={() => {
                                  navigator.clipboard.writeText(`_ddos_verification.${companyDomainForm}`)
                                    .then(() => showMessage('记录名称已复制到剪贴板', 'success'))
                                    .catch(() => showMessage('复制失败，请手动复制', 'error'));
                                }}
                              >
                                复制
                              </Button>
                            </Space>
                          </Col>
                        </Row>
                        <Row gutter={[16, 8]}>
                          <Col span={6}>
                            <Text strong>记录值：</Text>
                          </Col>
                          <Col span={18}>
                            <Space>
                              <Text code>{verificationRecord}</Text>
                              <Button 
                                type="default" 
                                size="small" 
                                icon={<CopyOutlined />}
                                onClick={() => {
                                  navigator.clipboard.writeText(verificationRecord)
                                    .then(() => showMessage('记录值已复制到剪贴板', 'success'))
                                    .catch(() => showMessage('复制失败，请手动复制', 'error'));
                                }}
                              >
                                复制
                              </Button>
                            </Space>
                          </Col>
                        </Row>
                        {verificationExpiration && (
                          <Row gutter={[16, 8]}>
                            <Col span={6}>
                              <Text strong>过期时间：</Text>
                            </Col>
                            <Col span={18}>
                              <Text>{new Date(verificationExpiration).toLocaleString()}</Text>
                            </Col>
                          </Row>
                        )}
                      </div>
                    </Card>

                    <Alert 
                      type="info"
                      style={{ marginBottom: '16px' }}
                      message={
                        <div>
                          <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
                            <ClockCircleOutlined style={{ marginRight: '8px' }} />
                            配置说明
                          </Title>
                          <ol style={{ margin: 0, paddingLeft: '20px' }}>
                            <li>登录您的DNS服务商管理控制台</li>
                            <li>找到域名DNS设置页面</li>
                            <li>添加上述TXT记录</li>
                            <li>等待DNS记录生效（通常需要5-60分钟）</li>
                            <li>点击下方"验证域名"按钮完成验证</li>
                          </ol>
                        </div>
                      }
                    />

                    <Alert 
                      type="warning"
                      message={
                        <div>
                          <Paragraph style={{ margin: 0 }}>
                            <ExclamationCircleOutlined style={{ marginRight: '8px' }} />
                            <Text strong>注意：</Text>DNS记录添加后可能需要几分钟到几小时才能生效，请耐心等待。
                          </Paragraph>
                        </div>
                      }
                    />
                  </div>
                )}
              </div>
            </Modal>

            {/* 密码重置模态框 */}
            <Modal 
              title={
                <span>
                  <LockOutlined style={{ marginRight: '8px' }} />
                  重置密码 - {selectedRAMUserForPassword?.displayName || selectedRAMUserForPassword?.username}
                  {selectedRAMUserForPassword?.domain && (
                    <Text type="secondary" style={{ marginLeft: '4px' }}>@{selectedRAMUserForPassword.domain}</Text>
                  )}
                </span>
              }
              open={showPasswordModal} 
              onCancel={handleClosePasswordModal}
              footer={[
                <Button key="cancel" onClick={handleClosePasswordModal}>
                  取消
                </Button>,
                <Button 
                  key="reset" 
                  type="primary" 
                  loading={passwordLoading}
                  disabled={!newPasswordForm.trim()}
                  onClick={handleResetPassword}
                  icon={<LockOutlined />}
                >
                  重置密码
                </Button>
              ]}
            >
              <Alert 
                message={
                  <div>
                    <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
                      <ExclamationCircleOutlined style={{ marginRight: '8px' }} />
                      安全提示
                    </Title>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      <li>重置密码后，该RAM账号需要使用新密码重新登录</li>
                      <li>如果该RAM账号当前在线，系统将静默强制其退出登录</li>
                      <li>建议将新密码安全地传达给用户</li>
                    </ul>
                  </div>
                }
                type="warning"
                showIcon
                style={{ marginBottom: '16px' }}
              />
              
              <Form layout="vertical">
                <Form.Item 
                  label="新密码"
                  help="密码长度至少为6位，建议包含数字、字母等多种字符"
                >
                  <Input.Password
                    placeholder="输入新密码（至少6位）"
                    value={newPasswordForm}
                    onChange={(e) => setNewPasswordForm(e.target.value)}
                    autoComplete="new-password"
                  />
                </Form.Item>
              </Form>
            </Modal>

            {/* 多点登录确认模态框 */}
            <Modal 
              title={
                <span>
                  <LoginOutlined style={{ marginRight: '8px' }} />
                  {multiLoginAction?.newPermission ? '开启' : '关闭'}多点登录权限
                </span>
              }
              open={showMultiLoginModal} 
              onCancel={handleCloseMultiLoginModal}
              footer={[
                <Button key="cancel" onClick={handleCloseMultiLoginModal}>
                  取消
                </Button>,
                <Button 
                  key="confirm"
                  type="primary"
                  loading={loading}
                  onClick={handleConfirmMultiLogin}
                  icon={<LoginOutlined />}
                >
                  确认{multiLoginAction?.newPermission ? '开启' : '关闭'}
                </Button>
              ]}
            >
              {multiLoginAction && (
                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <Paragraph><Text strong>RAM账号：</Text> {multiLoginAction.username}</Paragraph>
                    <Paragraph>
                      <Text strong>当前状态：</Text> 
                      <Tag color={multiLoginAction.currentPermission ? 'success' : 'default'} style={{ marginLeft: '8px' }}>
                        {multiLoginAction.currentPermission ? '允许多点登录' : '禁止多点登录'}
                      </Tag>
                    </Paragraph>
                    <Paragraph>
                      <Text strong>将要设置为：</Text> 
                      <Tag color={multiLoginAction.newPermission ? 'success' : 'default'} style={{ marginLeft: '8px' }}>
                        {multiLoginAction.newPermission ? '允许多点登录' : '禁止多点登录'}
                      </Tag>
                    </Paragraph>
                  </div>
                  
                  {multiLoginAction.newPermission ? (
                    <Alert 
                      message={
                        <div>
                          <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
                            <LoginOutlined style={{ marginRight: '8px' }} />
                            开启多点登录
                          </Title>
                          <Paragraph style={{ margin: 0 }}>
                            该RAM账号将可以在多个设备上同时保持登录状态，不会被互相踢下线。
                            这对于需要在多个设备上工作的用户很有用。
                          </Paragraph>
                        </div>
                      }
                      type="info"
                      showIcon
                    />
                  ) : (
                    <Alert 
                      message={
                        <div>
                          <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
                            <LogoutOutlined style={{ marginRight: '8px' }} />
                            关闭多点登录
                          </Title>
                          <Paragraph style={{ marginBottom: '8px' }}>
                            该RAM账号将只能在一个设备上保持登录状态，新登录会踢出之前的会话。
                          </Paragraph>
                          <Paragraph style={{ margin: 0 }}>
                            <Text strong>注意：</Text>如果该账号当前在多个设备上登录，
                            关闭此权限后会立即踢出除最新活动会话外的所有其他会话。
                          </Paragraph>
                        </div>
                      }
                      type="warning"
                      showIcon
                    />
                  )}
                </div>
              )}
            </Modal>
          </>
        )}
    </div>
  );
};

export default RAMAccountManagement; 